import klass from './SectionTabs.module.scss';
import { CustomEventWithDetail, type Html, html, View } from 'rune-ts';
import { font } from '../../../typography';
import { PostMessageNavigate } from '../../../app/navigate';
import { dataStr } from '../../../util/dataStr';

export type SectionTabType = {
  id: string;
  href: string;
  label: string;
  count?: string | number;
  klass?: string;
  preventDefault?: boolean;
};

export interface SectionTabsProps {
  current_tab_id: string;
  tabs: SectionTabType[];
  size?: 'xLarge' | 'large' | 'medium' | 'small' | 'xSmall';
  custom_klass?: string;
  navigate?: PostMessageNavigate;

  /**
   * @default 'underline'
   */
  variant?: 'underline' | 'bubble';
}

export interface SectionTabProps {
  tab: SectionTabType;
  size?: SectionTabsProps['size'];
  navigate?: SectionTabsProps['navigate'];
  active: boolean;
}

class SectionTab extends View<SectionTabProps> {
  static readonly ClickTabEvent = CustomEventWithDetail<{ tabId: string; view: SectionTab }>;

  protected override template(data: SectionTabProps): Html {
    const { tab, active, navigate } = data;

    return html`
      <a
        href="${tab.href}"
        data-id="${tab.id}"
        data-active="${active}"
        class="${klass.tab}"
        ${navigate ? html`data-post-message="${dataStr(navigate)}"` : ''}
        ${tab.preventDefault ? html`onclick="event.preventDefault()"` : ''}
      >
        <!-- label -->
        <span>${tab.label}</span>

        <!-- count -->
        ${tab.count ? html`<span class="${klass.count}">${tab.count || ''}</spanc>` : ''}
      </a>
    `;
  }

  public setActive(active: boolean) {
    this.data.active = active;
    return this;
  }

  public getId() {
    return this.data.tab.id;
  }

  public setLabel(label: string) {
    this.data.tab.label = label;
    return this;
  }

  protected override onRender() {
    return this.addEventListener('click', (e) => {
      if (!this.data.active) {
        this.dispatchEvent(SectionTab.ClickTabEvent, {
          bubbles: true,
          detail: {
            view: this,
            tabId: this.data.tab.id,
          },
        });
      } else {
        e.preventDefault();
      }
    });
  }
}

export class SectionTabs extends View<SectionTabsProps> {
  static readonly ChangeTabEvent = CustomEventWithDetail<{ tabId: string }>;
  static readonly UnderLineSizeOptions = {
    xLarge: {
      tab_font: font('28_bold'),
      size_class: `${klass.xLarge}`,
      variant_class: `${klass.underline}`,
    },
    large: {
      tab_font: font('24_bold'),
      size_class: `${klass.large}`,
      variant_class: `${klass.underline}`,
    },
    medium: {
      tab_font: font('20_bold'),
      size_class: `${klass.medium}`,
      variant_class: `${klass.underline}`,
    },
    small: {
      tab_font: font('16_medium'),
      size_class: `${klass.small}`,
      variant_class: `${klass.underline}`,
    },
    xSmall: {
      tab_font: font('14_medium'),
      size_class: `${klass.xSmall}`,
      variant_class: `${klass.underline}`,
    },
  } as const;

  static readonly BubbleSizeOptions = {
    xLarge: {
      tab_font: '',
      size_class: '',
      variant_class: '',
    },
    large: {
      tab_font: font('18_medium'),
      size_class: `${klass.large}`,
      variant_class: `${klass.bubble}`,
    },
    medium: {
      tab_font: font('16_medium'),
      size_class: `${klass.medium}`,
      variant_class: `${klass.bubble}`,
    },
    small: {
      tab_font: font('14_medium'),
      size_class: `${klass.small}`,
      variant_class: `${klass.bubble}`,
    },
    xSmall: {
      tab_font: '',
      size_class: '',
      variant_class: '',
    },
  } as const;

  public tabViews = this.makeSectionTabViews();

  protected override template(data: SectionTabsProps) {
    const { custom_klass = '' } = data;
    const { tab_font, size_class, variant_class } = this.size_options[this.size];

    return html`<div class="${klass.tabs} ${variant_class} ${tab_font} ${size_class} ${custom_klass}">
      ${this.tabViews}
    </div>`;
  }

  private makeSectionTabViews() {
    return this.data.tabs.map(
      (tab) =>
        new SectionTab({
          tab,
          size: this.data.size,
          active: tab.id === this.data.current_tab_id,
          navigate: this.data.navigate,
        }),
    );
  }

  get size() {
    return this.data.size || 'small';
  }

  get variant() {
    return this.data.variant || 'underline';
  }

  private get size_options() {
    switch (this.variant) {
      case 'bubble':
        return SectionTabs.BubbleSizeOptions;
      case 'underline':
        return SectionTabs.UnderLineSizeOptions;
      default:
        return SectionTabs.UnderLineSizeOptions;
    }
  }

  protected override onRender() {
    return this.delegate(SectionTab.ClickTabEvent, SectionTab, (e) => {
      this.tabViews.forEach((view) => {
        view.setActive(e.detail.tabId === view.data.tab.id);
      });
      this.redraw();
      this.dispatchEvent(SectionTabs.ChangeTabEvent, {
        bubbles: true,
        detail: {
          tabId: e.detail.tabId,
        },
      });
    });
  }
}
